import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { makeRequest } from "../../utils/request";
import { createRestSlice, ItemState } from "./restSlice";

export type AgentEnvironment = {
  id: string;
  agentId: string;
  name: string;
  functionId: string;
  recordingEnabled: boolean;
  recordingLocation: "local" | null;
  redactionEnabled: boolean;
  tracingEnabled: boolean;
  sttModel?: string;
  sttEndpointing?: number;
  sttExternalEndpointing?: number;
  sttTurnTakingMode?: "provider" | "silero" | "smart_turn";
  smartTurnVadStopSecs?: number | null;
  smartTurnStopSecs?: number | null;
  smartTurnTimeout?: number | null;
  sttLanguage: string;
  sttPrompt: string;
  audioInputDelay?: number;
  sttNoiseReductionType?: string;
  sttFallbackModel?: string | null;
  sttFilter?: string;
  errorFallbackType?: "phone" | "webhook";
  errorFallbackValue?: string;
  errorFallbackTimeout?: number;
  ringingEnabled?: boolean;
  optimizerId?: string;
  isDebug?: boolean;
  startupAudioUrl?: string;
  startupAudioTranscription?: string;
  startupAudioInterruptible?: boolean;
  startupConnectionTimeoutSeconds?: number;
  webhookUrl?: string | null;
  webhookSecret?: string | null;
  region?: string;
  allowedOrigins?: string[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  deploymentAction?: string; // Type of deployment action: "function" or "config"
  deploymentDedicated?: boolean; // Whether to deploy a dedicated deployment
};

const configurePhone = createAsyncThunk("agentEnvironment/configurePhone", async ({ agentId, id }: Partial<AgentEnvironment>) => {
  const { data } = await makeRequest<{ data: AgentEnvironment }>(`/v1/agents/${agentId}/environments/${id}/configure-phone`, {
    method: "POST",
  });

  return data;
});

const regenerateWebhookSecret = createAsyncThunk("agentEnvironment/regenerateWebhookSecret", async ({ agentId, id }: Partial<AgentEnvironment>) => {
  const { data } = await makeRequest<{ data: { webhookSecret: string } }>(`/v1/agents/${agentId}/environments/${id}/regenerate-webhook-secret`, {
    method: "POST",
  });

  return { id, webhookSecret: data.webhookSecret };
});

const agentEnvironmentSlice = createRestSlice<AgentEnvironment>("agentEnvironments", "/v1/agents/:agentId/environments", {
  extraReducers: (builder) => {
    builder.addCase(configurePhone.pending, (state: ItemState<AgentEnvironment>) => {
      state.isLoading = true;
    });

    builder.addCase(configurePhone.fulfilled, (state: ItemState<AgentEnvironment>, action: PayloadAction<AgentEnvironment>) => {
      state.isLoading = false;
      state.entities[action.payload.id] = action.payload;
    });

    builder.addCase(configurePhone.rejected, (state: ItemState<AgentEnvironment>) => {
      state.isLoading = false;
    });

    builder.addCase(regenerateWebhookSecret.fulfilled, (state: ItemState<AgentEnvironment>, action: PayloadAction<{ id: string; webhookSecret: string }>) => {
      const { id, webhookSecret } = action.payload;
      if (state.entities[id]) {
        state.entities[id].webhookSecret = webhookSecret;
      }
    });
  },
});

export const agentEnvironmentReducer = agentEnvironmentSlice.reducer;
export const agentEnvironmentActions = {
  ...agentEnvironmentSlice.actions,
  configurePhone,
  regenerateWebhookSecret,
};
